import React, { useMemo } from "react";
import s from "./index.module.css";
import { utils } from "../../utils";
import { AppLogoText } from "../../components";
import { MdOutlineReviews, MdWallet } from "react-icons/md";
import { FaStore, FaLocationDot, FaGlobe, FaDownload } from "react-icons/fa6";

// Memoized image component to prevent unnecessary re-renders
const ShopImage = React.memo(({ src, alt, className }) => (
  <img
    src={src}
    alt={alt}
    className={className}
    loading="lazy"
    decoding="async"
  />
));

ShopImage.displayName = "ShopImage";

export const ShopFooter = ({ shopInfo = {}, config = {} }) => {
  // Memoize computed values to avoid recalculations
  const logoImage = useMemo(
    () => (shopInfo?.images?.[0] ? utils.getImageUrl(shopInfo.images[0]) : ""),
    [shopInfo?.images],
  );

  const additionalImages = useMemo(
    () => shopInfo?.images?.slice(1) || [],
    [shopInfo?.images],
  );

  const storeUrl = useMemo(
    () => utils.getStoreUrl(shopInfo?.primary_num, shopInfo?.shop_id),
    [shopInfo?.primary_num, shopInfo?.shop_id],
  );

  const websiteUrl = useMemo(() => {
    if (!shopInfo?.website) return "";

    return shopInfo.website.startsWith("http")
      ? shopInfo.website
      : `https://${shopInfo.website}`;
  }, [shopInfo?.website]);

  // Don't render if no shop info
  if (!shopInfo || Object.keys(shopInfo).length === 0) {
    return null;
  }


  return (
    <div className={s.shopInfoContainer}>
      {/* Shop Images Gallery */}
      {additionalImages.length > 0 && (
        <div className={s.shopImages} role="region" aria-label="Shop gallery">
          {additionalImages.map((img, idx) => (
            <ShopImage
              key={`shop-img-${idx}`}
              src={utils.getImageUrl(img)}
              alt={`Shop view ${idx + 2}`}
              className={s.shopImage}
            />
          ))}
        </div>
      )}

      <div className={s.descriptionContainer}>
        {/* About / Description Section */}
        <div className={s.descriptionColumn}>
          <header className={s.header}>
            <div className={s.logo} aria-label="Store logo">
              {logoImage ? (
                <ShopImage
                  src={logoImage}
                  alt={`${shopInfo?.name || "Store"} logo`}
                  className={s.logoImage}
                />
              ) : (
                <FaStore className={s.storeIcon} aria-hidden="true" />
              )}
            </div>
            <div className={s.descriptionTitle}>
              <h2 className={s.title}>{shopInfo?.name || "Store Name"}</h2>
              {shopInfo?.category && (
                <p className={s.tagline}>{shopInfo.category}</p>
              )}
            </div>
          </header>

          {shopInfo.description && (
            <p className={s.description}>{shopInfo.description}</p>
          )}
        </div>

        {/* Location */}
        {shopInfo.city && shopInfo.state && (
          <div className={s.link}>
            <span className={s.tag}>
              <FaLocationDot className={s.icon} aria-hidden="true" />
              <span className={s.linkText}>
                {shopInfo.city}, {shopInfo.state}
              </span>
            </span>
          </div>
        )}

        {!!(config?.review_link || shopInfo?.upi_id) && (
          <div className={s.footer_cta_container}>
          {
            config?.review_link && (
              <a 
                target="_blank"
                className={s.review_link}
                rel="noopener noreferrer"
                href={config.review_link}
              >
                <MdOutlineReviews className={s.icon} aria-hidden="true" />
                Write Review
              </a>
            )
          }
          {
            shopInfo?.upi_id && (
              <a 
                target="_blank"
                rel="noopener noreferrer"
                href={utils.getUpiUrl(shopInfo)}
                className={`${s.review_link} ${s.payment_link}`}
              >
                <MdWallet className={`${s.icon} ${s.upi_icon}`} aria-hidden="true" />
                Pay Now
              </a>
            )
          }
          </div>
        )}

        {/* Website Link */}
        {websiteUrl && (
          <div className={s.link}>
            <span className={s.tag}>
              <FaGlobe className={s.icon} aria-hidden="true" />
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={s.website}
              >
                {shopInfo.website}
              </a>
            </span>
          </div>
        )}

        {/* Powered By */}
        <div className={s.link}>
          <span className={s.tag}>
            <FaDownload className={s.icon} aria-hidden="true" />
            <span className={s.linkText}>
              {"Powered by "}
              <a
                href={storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={s.poweredLink}
              >
                <AppLogoText />
              </a>
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

ShopFooter.displayName = "ShopFooter";
